import { useEffect, useState } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type { Categoria } from '../tipos';

/**
 * Catálogo de categorias do marketplace.
 *
 * Existe porque a comissão é por categoria, e categoria em texto livre não casa
 * com tabela nenhuma: "Áudio", "audio" e "Áudio " seriam três — e a comissão
 * sumiria sem ninguém perceber.
 *
 * Guarda só o nome, espelhando o Mercado Livre. NCM não entra aqui: serve à
 * importação e vai virar módulo próprio.
 */

export type RascunhoCategoria = Omit<
  Categoria,
  'id' | 'criadoEm' | 'criadoPor' | 'atualizadoEm' | 'atualizadoPor'
>;

export function categoriaVazia(): RascunhoCategoria {
  return { nome: '', ativo: true, observacoes: null };
}

export function useCategorias() {
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(
    () =>
      onSnapshot(
        query(collection(db, 'categorias'), orderBy('nome')),
        (s) => {
          setCategorias(s.docs.map((d) => ({ id: d.id, ...d.data() }) as Categoria));
          setCarregando(false);
        },
        (e) => {
          setErro(e.message);
          setCarregando(false);
        },
      ),
    [],
  );

  return { categorias, carregando, erro };
}

export async function salvarCategoria(
  id: string | null,
  dados: RascunhoCategoria,
  usuarioId: string,
): Promise<string> {
  const ref = id ? doc(db, 'categorias', id) : doc(collection(db, 'categorias'));

  if (id) {
    await updateDoc(ref, { ...dados, atualizadoEm: serverTimestamp(), atualizadoPor: usuarioId });
  } else {
    await setDoc(ref, {
      ...dados,
      criadoEm: serverTimestamp(),
      criadoPor: usuarioId,
      atualizadoEm: null,
      atualizadoPor: null,
    });
  }
  return ref.id;
}

/**
 * Apagar é destrutivo de um jeito silencioso: produtos que apontam para esta
 * categoria ficariam órfãos e a análise deles perderia a comissão sem aviso.
 * Por isso quem chama precisa passar quantos produtos usam a categoria.
 */
export async function removerCategoria(id: string, produtosQueUsam: number): Promise<void> {
  if (produtosQueUsam > 0) {
    throw new Error(
      `${produtosQueUsam} ${produtosQueUsam === 1 ? 'mercadoria usa' : 'mercadorias usam'} esta categoria. ` +
        'Troque a categoria delas antes de apagar, ou desative a categoria em vez de apagá-la.',
    );
  }
  await deleteDoc(doc(db, 'categorias', id));
}
